import { useMemo } from "react";
import { ChevronLeft, Plus, X } from "lucide-react";
import { Icon, Picker, type PickerOption, Textarea, useRequestClose } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useInvitableRows, type AssignableRow } from "../../app/hooks/useAssignable.js";
import { inputToCents } from "../../app/hooks/useOpportunityMoney.js";
import { formatMoneyCents } from "../../app/hooks/inboxFormat.js";
import { PersonAvatar } from "../shared/PersonAvatar.js";
import { initials } from "../../shells/mobile/accountRoles.js";
import base from "./CalendarEventDetails.module.scss";
import styles from "./WhosPlaying.module.scss";

/** The note every invitee reads — the server's ceiling (`HoldInvitesService.NoteMax`). */
const NOTE_MAX = 280;

export interface WhosPlayingLine {
  person: AssignableRow;
  pay: string;
}

/** What has been picked so far. The caller holds it, so going back to the request's Details keeps everyone added. */
export interface WhosPlayingDraft {
  lines: WhosPlayingLine[];
  note: string;
}

export const EMPTY_WHOS_PLAYING: WhosPlayingDraft = { lines: [], note: "" };

export interface HoldInvitePick {
  personId: string;
  payCents: number | null;
}

/**
 * "Who's playing" (ART-342, design 1a–1c) — Hold & add crew's people-and-pay step, on desktop AND the phone. It sits
 * inside the show request's own Details shell (a pop-up on desktop, a sheet on the phone) in place of opening the full
 * booking editor, which stays one tap away from the Needs attention row once the invites are out.
 *
 * It starts empty: search the people this show can ask (`useInvitableRows`), add them, give each an optional pay —
 * each amount becomes that person's Member pay line in section 09 · Expenses, the same record the editor writes — and
 * Send invites. Nothing is answered until Send: Cancel or ✕ leaves the request unanswered, and ‹ Details goes back to
 * it with everyone still added. Pay fields are drawn only for a seat holding show money; the server refuses a figure
 * from anyone else.
 */
export function WhosPlaying({ venue, date, draft, onDraft, onBack, onClose, onSend, sending }: {
  venue: string;
  /** "Sat, Dec 5", already formatted. */
  date: string | null;
  draft: WhosPlayingDraft;
  onDraft: (next: WhosPlayingDraft) => void;
  /** ‹ Details — absent when there is no request to go back to. */
  onBack?: () => void;
  onClose: () => void;
  onSend: (people: HoldInvitePick[], note: string | null) => void;
  sending: boolean;
}) {
  const full = useCapability("money:full");
  const perShow = useCapability("money:per_show");
  const money = full || perShow;
  // ✕ and Cancel leave through the shell's one exit, so added people are not thrown away without asking.
  const requestClose = useRequestClose();
  const close = () => {
    if (requestClose) requestClose();
    else onClose();
  };
  const candidates = useInvitableRows();
  const { lines, note } = draft;
  const setLines = (next: WhosPlayingLine[]) => onDraft({ ...draft, lines: next });

  // The search is the house `Picker` used as an ADD trigger (`ChipPicker`'s shape, with our own rows in place of
  // chips): its value stays empty, focusing it lists EVERYONE attached to the artist, typing narrows it, and a pick
  // appends a line. The list portals out of the dialog and positions itself against the viewport, so the dialog's
  // scrolling pane can never clip it, whatever is added below (ART-342 feedback, three rounds of a hand-rolled list
  // that lived inside the pane). On a phone it is the shared bottom-sheet picker.
  const options = useMemo((): PickerOption[] => {
    const taken = new Set(lines.map((l) => l.person.personId));
    const rows = candidates.rows
      // The artist account is the band itself, never a person on the show (ART-145's has_inbox rule, one surface over).
      .filter((r) => !taken.has(r.personId) && !r.isOwner);
    // An empty list says why, as a disabled row, rather than a bare "No matches". Its value is not the picker's
    // empty value, or the input would show it as the current choice.
    if (rows.length === 0) {
      const why = candidates.isLoading ? "Loading…" : "Everyone is already added";
      return [{ value: "none", label: why, disabled: true }];
    }
    return rows.map((r) => {
      const role = [r.jobLabel, r.group].filter(Boolean).join(" · ");
      return {
        value: r.personId,
        // The label is what typing filters on, so it carries the role and party as well as the name.
        label: [r.displayName, role].filter(Boolean).join(" · "),
        render: (
          <span className={styles.result}>
            <PersonAvatar personId={r.personId} avatarMediaId={r.avatarMediaId} initials={initials(r.displayName)} size="sm" />
            <span className={styles.who}>
              <span className={styles.name}>{r.displayName}</span>
              {role && <span className={styles.role}>{role}</span>}
            </span>
            <span className={styles.add} aria-hidden>
              <Icon icon={Plus} size={16} /> Add
            </span>
          </span>
        ),
      };
    });
  }, [lines, candidates.rows, candidates.isLoading]);
  const add = (personId: string) => {
    const person = candidates.rows.find((r) => r.personId === personId);
    if (person) setLines([...lines, { person, pay: "" }]);
  };

  const total = lines.reduce((sum, l) => sum + (inputToCents(l.pay) ?? 0), 0);
  const meta = [venue, date, "invites go out as a hold"].filter(Boolean).join(" · ");

  function send() {
    onSend(
      lines.map((l) => ({ personId: l.person.personId, payCents: money ? inputToCents(l.pay) : null })),
      note.trim() || null,
    );
  }

  return (
    <>
      <div className={base.scroll}>
        <header className={base.head}>
          <div className={base.headRow}>
            {onBack && (
              <button type="button" className={styles.back} onClick={onBack}>
                <Icon icon={ChevronLeft} size={16} /> Details
              </button>
            )}
            <button type="button" className={base.close} onClick={close} aria-label="Close">
              <Icon icon={X} size={20} />
            </button>
          </div>
          <h2 className={base.title}>Who&apos;s playing</h2>
          <p className={base.date}>{meta}</p>
        </header>

        <div className={styles.body}>
          <div data-no-dirty="">
            <Picker
              label="Search people"
              hideLabel
              typeahead
              value=""
              onChange={add}
              options={options}
              placeholder="Search by name or role"
            />
          </div>

          {lines.length === 0 ? (
            <div className={styles.empty}>
              <p className={styles.emptyTitle}>No one added yet</p>
              <p className={styles.emptySub}>Search to add band and crew</p>
            </div>
          ) : (
            <section className={styles.added} aria-label="Who's playing">
              <div className={styles.addedHead}>
                <span className={styles.count}>{lines.length} added</span>
                {money && lines.length > 1 && (
                  <button
                    type="button"
                    className={styles.samePay}
                    onClick={() => setLines(lines.map((l) => ({ ...l, pay: lines[0]!.pay })))}
                  >
                    Same pay for all
                  </button>
                )}
              </div>
              <ul className={styles.lines}>
                {lines.map((l, i) => (
                  <li key={l.person.personId} className={styles.line}>
                    <PersonAvatar personId={l.person.personId} avatarMediaId={l.person.avatarMediaId} initials={initials(l.person.displayName)} size="sm" />
                    <span className={styles.who}>
                      <span className={styles.name}>{l.person.displayName}</span>
                      <span className={styles.role}>{l.person.jobLabel}</span>
                    </span>
                    {money && (
                      <span className={styles.pay}>
                        <span aria-hidden>$</span>
                        <input
                          className={styles.payInput}
                          inputMode="decimal"
                          aria-label={`Pay for ${l.person.displayName}`}
                          value={l.pay}
                          placeholder="0"
                          onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, pay: e.target.value } : x)))}
                        />
                      </span>
                    )}
                    <button
                      type="button"
                      className={styles.remove}
                      aria-label={`Remove ${l.person.displayName}`}
                      onClick={() => setLines(lines.filter((_, j) => j !== i))}
                    >
                      <Icon icon={X} size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <Textarea
            id="whos-playing-note"
            label="Note to the team"
            labelAside="Optional"
            labelVariant="quiet"
            rows={2}
            maxLength={NOTE_MAX}
            value={note}
            onChange={(e) => onDraft({ ...draft, note: e.target.value })}
          />
        </div>
      </div>

      <footer className={base.actions}>
        {money && (
          <p className={styles.summary}>
            <span className={styles.summaryLabel}>Member pay</span>{" "}
            <span className={styles.summaryTotal}>{formatMoneyCents(total)}</span>
            {total > 0 && <span className={styles.summaryNote}> · added to Expenses</span>}
          </p>
        )}
        <div className={base.buttons}>
          <button type="button" className={base.btnGhost} onClick={close} disabled={sending}>
            Cancel
          </button>
          <button type="button" className={styles.send} disabled={lines.length === 0 || sending} onClick={send}>
            Send invites
          </button>
        </div>
      </footer>
    </>
  );
}
